import Dexie from 'dexie';

export const CATEGORIES = ['Hospital', 'Shop', 'Water Point', 'Fuel', 'School'];

export class MarkerDatabase extends Dexie {
  markers;
  pois;
  places;
  routes;

  constructor() {
    super('m2_markers');
    this.version(1).stores({
      markers: 'id, category, lat, lng, [lat+lng]',
    });
    // Version 2 previously added ride-recording tables (rides/breadcrumbs).
    // Those tables are intentionally removed; only markers and cached POIs remain.
    this.version(2).stores({
      markers: 'id, category, lat, lng, [lat+lng]',
      pois: 'id, category, lat, lng, [lat+lng], fetchedAt',
    });
    this.version(3).stores({
      markers: 'id, category, lat, lng, [lat+lng]',
      pois: 'id, category, lat, lng, [lat+lng], fetchedAt',
      places: 'id, name, lat, lng, cachedAt',
      routes: 'key, fromLng, fromLat, toLng, toLat, cachedAt',
    });
  }

  /**
   * Query markers inside a bounding box using the latitude index plus
   * an in-memory longitude filter. Returns a plain array of marker objects.
   */
  async getInBounds([swLng, swLat, neLng, neLat], activeCategories = null) {
    try {
      let collection = this.markers.where('lat').between(swLat, neLat);
      // Dexie compound index on [lat+lng] cannot do 2D range filtering.
      const lngWrap = swLng > neLng; // crossing antimeridian
      const rows = await collection.toArray();
      return rows.filter((m) => {
        if (activeCategories && !activeCategories.has(m.category)) return false;
        if (lngWrap) return m.lng >= swLng || m.lng <= neLng;
        return m.lng >= swLng && m.lng <= neLng;
      });
    } catch (err) {
      console.error('[DB] bounds query failed', err);
      throw err;
    }
  }

  async upsertBulk(items) {
    try {
      return await this.markers.bulkPut(items);
    } catch (err) {
      console.error('[DB] bulkPut failed', err);
      throw err;
    }
  }

  async clearMarkers() {
    return this.markers.clear();
  }

  async countMarkers() {
    try {
      return await this.markers.count();
    } catch (err) {
      console.error('[DB] count failed', err);
      return 0;
    }
  }

  async upsertPOIs(items) {
    if (!items.length) return;
    await this.pois.bulkPut(items);
    await this.trimPOIs(50000);
  }

  async trimPOIs(maxCount = 100000) {
    try {
      const total = await this.pois.count();
      if (total <= maxCount) return;
      const toDelete = total - maxCount;
      const oldest = await this.pois.orderBy('fetchedAt').limit(toDelete).primaryKeys();
      if (oldest.length) await this.pois.bulkDelete(oldest);
    } catch (err) {
      console.error('[DB] trim POIs failed', err);
    }
  }

  async upsertPlace(place) {
    await this.places.put({ ...place, cachedAt: Date.now() });
  }

  async searchPlaces(query, limit = 20) {
    const q = query.toLowerCase().trim();
    if (!q) return [];
    const all = await this.places.toArray();
    return all
      .filter((p) => p.name.toLowerCase().includes(q))
      .sort((a, b) => (b.cachedAt || 0) - (a.cachedAt || 0))
      .slice(0, limit);
  }

  routeKey(from, to) {
    const round = (n) => Number(n).toFixed(4);
    return `${round(from[0])},${round(from[1])};${round(to[0])},${round(to[1])}`;
  }

  async cacheRoute(from, to, route) {
    const key = this.routeKey(from, to);
    await this.routes.put({
      key,
      fromLng: from[0],
      fromLat: from[1],
      toLng: to[0],
      toLat: to[1],
      route,
      cachedAt: Date.now(),
    });
  }

  async getCachedRoute(from, to) {
    const key = this.routeKey(from, to);
    return this.routes.get(key);
  }

  async getPOIs() {
    return this.pois.toArray();
  }

  async getPOIsInBounds([swLng, swLat, neLng, neLat], activeCategories = null) {
    const rows = await this.pois.where('lat').between(swLat, neLat).toArray();
    const wraps = swLng > neLng;
    return rows.filter((poi) => {
      if (activeCategories && !activeCategories.has(poi.osmCategory)) return false;
      return wraps ? poi.lng >= swLng || poi.lng <= neLng : poi.lng >= swLng && poi.lng <= neLng;
    });
  }
}

export const db = new MarkerDatabase();
